// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React from 'react';
import './BaleView.scss';
import { useTranslation } from 'react-i18next';
import { firestore, storage } from '../../context/firebase';
import { onSnapshot, setDoc, doc } from 'firebase/firestore';
import Switch from 'react-switch';
import { isMobileOnly } from 'react-device-detect';
import { Modal } from 'react-bootstrap';
import { CheckCircle, AlertTriangle } from 'lucide-react';

import { fetchImageUrl } from '../../shared_components/utils/ImgUtils';
import BaleCard from '../../components/BaleCard/BaleCard.jsx';
import LoadingIcon from '../../components/LoadingIcon/LoadingIcon.jsx';
import LabelingPanel from '../../shared_components/LabelingPanel/LabelingPanel.jsx';
import PropertyField from '../../components/PropertyField/PropertyField';
import SwipeableContainer from '../SwipeableContainer/SwipeableContainer.jsx';
import DetectionsView from '../DetectionsView/DetectionsView.jsx';

// Importing icons
import back_icon from '../../assets/icons/white/back_small.png';
import notes_icon from '../../assets/icons/white/document.png';
import alert_off_icon from '../../assets/icons/white/alert_no_circle.png';
import alert_on_icon from '../../assets/icons/red/alert.png';
import close_icon from '../../assets/icons/white/close.svg';
import { USER_DOCUMENT_PATH } from '../../shared_components/utils/FirebaseUtils.js';
import { useAuthStore } from '../../stores/authStore.js';
import { getLabelDisplayName } from '../../shared_components/utils/FirebaseLabelsetsUtils';

export default function BaleView({
  bales,
  activeBaleId,
  setActiveBaleId,
  getPreviousBale,
  getNextBale,
  handleFieldChange,
  onCloseBaleView,
  activeBaleDocRef,
}) {
  const { i18n, t } = useTranslation();
  const currentUser = useAuthStore((state) => state.currentUser);
  const globalLabelsets = useAuthStore((state) => state.globalLabelsets);
  const defaultLabelsets = useAuthStore((state) => state.defaultLabelsets);
  const deliverySettings = useAuthStore((state) => state.deliverySettings);
  const [baleImageData, setBaleImageData] = React.useState(null);
  const [imageSize, setImageSize] = React.useState({ width: 0, height: 0, scale: 1 });
  const [screenSize, setScreenSize] = React.useState({ width: window.innerWidth, height: window.innerHeight });
  const [userSettings, setUserSettings] = React.useState({
    show_labeling_panel: isMobileOnly ? false : (currentUser?.info?.app_settings?.show_labeling_panel ?? false),
  });
  const [baleDocData, setBaleDocData] = React.useState(null);

  React.useEffect(() => {
    // Handle window resize to correctly size image and bounding boxes
    function handleResize() {
      setScreenSize({ width: window.innerWidth, height: window.innerHeight });
    }
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [activeBaleId]);

  // Process detected objects into bounding boxes
  const detectionBoxes = React.useMemo(() => {
    const MIN_CONFIDENCE = 0.5;
    if (!bales[activeBaleId]?.detections?.detected_objects) return {};

    const padding = bales[activeBaleId]?.bbox_frame_border ?? 0;
    return Object.entries(bales[activeBaleId].detections.detected_objects).reduce((acc, [id, detection]) => {
      if (!detection?.xyxy || detection.xyxy.length < 4 || !detection.cls || !detection.labelset) return acc;
      if (detection.conf < MIN_CONFIDENCE) return acc;

      acc[id] = {
        bbox: [
          detection.xyxy[0] + padding,
          detection.xyxy[1] + padding,
          detection.xyxy[2] + padding,
          detection.xyxy[3] + padding,
        ],
        cls: detection.cls,
        conf: detection.conf,
        labelset: detection.labelset,
        display_name: getLabelDisplayName(globalLabelsets?.[detection.labelset], detection.cls, i18n.language),
        display_color: globalLabelsets?.[detection.labelset]?.labels?.[detection.cls]?.display_color,
      };
      return acc;
    }, {});
  }, [bales, activeBaleId, globalLabelsets]);

  // Subscribe to bale document data to get image issues
  React.useEffect(() => {
    if (!activeBaleDocRef) return;

    const unsubscribe = onSnapshot(activeBaleDocRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        setBaleDocData(data);
      } else {
        setBaleDocData(null);
      }
    });

    return () => unsubscribe();
  }, [activeBaleDocRef]);

  function getImageStoragePath(bale) {
    return bale?.image_storage_path ?? null;
  }

  function getImageIssues() {
    /* Get image issues from the bale document data */
    const imageIssues = baleDocData?.predicted_labels?.image_issues?.class;

    if (!imageIssues) {
      return null; // No data available yet
    }

    if (imageIssues === 'no_issues') {
      return { isGood: true, issues: [] };
    }

    // If it's an array, return the issues
    if (Array.isArray(imageIssues)) {
      return { isGood: false, issues: imageIssues };
    }

    // If it's a string but not "no_issues", treat it as a single issue
    return { isGood: false, issues: [imageIssues] };
  }

  React.useEffect(() => {
    /* Load the image whenever the image URL changes */
    const imageStoragePath = getImageStoragePath(bales?.[activeBaleId]);
    if (!imageStoragePath) {
      return;
    }

    setBaleImageData(null);
    fetchImageUrl(storage, imageStoragePath).then((url) => {
      const img = new Image();
      img.src = url;
      img.onload = () => {
        setBaleImageData(img);
      };
    });
  }, [bales, activeBaleId]);

  React.useEffect(() => {
    /* Calculate image size based on screen size and image aspect ratio */
    if (!baleImageData) {
      return;
    }

    const REM_PX = parseFloat(getComputedStyle(document.documentElement).fontSize);
    const WIDTH_LABELING_PANEL = userSettings?.show_labeling_panel ? 18 * REM_PX : 0;
    const WIDTH_BORDER = 2 * REM_PX;
    const MAX_WIDTH_IMAGE = screenSize.width - WIDTH_LABELING_PANEL - WIDTH_BORDER;

    const HEIGHT_HEADER = 6 * REM_PX;
    const HEIGHT_FOOTER = 7 * REM_PX;
    const HEIGHT_BORDER = 3 * REM_PX;
    const MAX_HEIGHT_IMAGE = screenSize.height - HEIGHT_HEADER - HEIGHT_FOOTER - HEIGHT_BORDER;

    let scale = MAX_WIDTH_IMAGE / baleImageData.width;
    if (baleImageData.height * scale > MAX_HEIGHT_IMAGE) {
      scale = MAX_HEIGHT_IMAGE / baleImageData.height;
    }
    setImageSize({ width: baleImageData.width * scale, height: baleImageData.height * scale, scale: scale });
  }, [baleImageData, screenSize, userSettings]);

  React.useEffect(() => {
    /* Load user settings (to load labeling panel view) - but only on large devices */
    if (isMobileOnly) {
      // On mobile devices, the labeling panel is shown in a modal and closed by default
      return;
    }

    const unsubscribe = onSnapshot(doc(firestore, USER_DOCUMENT_PATH(currentUser.uid)), (snapshot) => {
      setUserSettings(snapshot.data()?.app_settings ?? {});
    });

    return () => unsubscribe();
  }, []);

  function toggleLabelingView() {
    if (isMobileOnly) {
      // On mobile devices, the labeling panel is shown in a modal and closed by default. No syncing with Firestore
      setUserSettings((prev) => ({ show_labeling_panel: !prev.show_labeling_panel }));
      return;
    }

    setDoc(
      doc(firestore, USER_DOCUMENT_PATH(currentUser.uid)),
      {
        app_settings: {
          show_labeling_panel: !(userSettings.show_labeling_panel ?? false),
        },
      },
      { merge: true },
    );
  }

  const showDetections =
    (deliverySettings?.features?.ai_visualization ?? false) && (currentUser?.info?.user_flags || [])?.includes('admin');

  return (
    <div className='bale-view'>
      <div className='bale--header'>
        <div onClick={onCloseBaleView} className='bale--back'>
          <img src={back_icon} alt='Back' className='bale--back-icon' />
        </div>
        {bales && (
          <div className='bales-scroll-bar'>
            {Object.entries(bales)
              .sort((a, b) => {
                return a[1]?.flagged && !b[1]?.flagged
                  ? -1
                  : !a[1]?.flagged && b[1]?.flagged
                    ? 1
                    : a[0].localeCompare(b[0]);
              })
              .map(([id, bale]) => {
                // Extract deliveryId from activeBaleDocRef path
                const deliveryId = activeBaleDocRef?.path?.split('/')[3] || null;

                return (
                  <div
                    className={`bale-card-wrapper ${activeBaleId === id && 'active-bale'}`}
                    onClick={() => {
                      setActiveBaleId(id);
                    }}
                    key={id}
                  >
                    <BaleCard
                      key={id}
                      imageStoragePath={getImageStoragePath(bale)}
                      flagged={bale?.flagged}
                      baleId={id}
                      deliveryId={deliveryId}
                    />
                  </div>
                );
              })}
          </div>
        )}
        {currentUser.info.user_flags &&
          ['labeler', 'admin'].some((flag) => currentUser.info.user_flags.includes(flag)) && (
            <div className='bale-toggle-labeling'>
              <p>{t('labeling_panel_toggle')}</p>
              <Switch
                checked={userSettings.show_labeling_panel ?? false}
                onChange={toggleLabelingView}
                uncheckedIcon={false}
                checkedIcon={false}
              />
            </div>
          )}
      </div>
      <div className='bale--center-section'>
        <SwipeableContainer
          className='bale--img-container'
          style={{
            width: imageSize.width ?? screenSize.width * 0.9,
            height: imageSize.height ?? screenSize.height * 0.5,
          }}
          onSwipeLeft={getPreviousBale}
          onSwipeRight={getNextBale}
        >
          <DetectionsView imageData={baleImageData} boundingBoxes={detectionBoxes} showDetections={showDetections} />
          <div className='bale--img-loading-container'>
            {!getImageStoragePath(bales?.[activeBaleId]) && <LoadingIcon />}
          </div>

          <div
            className='bale-alert'
            onClick={() => {
              handleFieldChange({ target: { name: 'flagged', value: !bales[activeBaleId].flagged } });
            }}
            style={{
              opacity: bales[activeBaleId]?.flagged ? 1 : 0.5,
              display: getImageStoragePath(bales?.[activeBaleId]) ? 'block' : 'none',
            }}
          >
            <img src={bales[activeBaleId]?.flagged ? alert_on_icon : alert_off_icon} />
          </div>
        </SwipeableContainer>
        {/* Image Quality Indicator */}
        {getImageStoragePath(bales?.[activeBaleId]) &&
          (() => {
            const imageIssues = getImageIssues();

            if (!imageIssues) return null;

            return (
              <div className='bale-image-quality-indicator'>
                {imageIssues.isGood ? (
                  <div className='bale-image-quality-good'>
                    <CheckCircle size={20} />
                    <span className='bale-image-quality-text'>{t('analysis_ready') || 'Analysis Ready'}</span>
                  </div>
                ) : (
                  <div className='bale-image-quality-issues'>
                    <div className='bale-image-quality-warning'>
                      <AlertTriangle size={20} />
                      <span className='bale-image-quality-text'>{t('image_not_analyzed') || 'Image Not Analyzed'}</span>
                    </div>
                    <div className='bale-image-quality-details'>
                      {imageIssues.issues.map((issue, index) => {
                        let issueDisplayName =
                          getLabelDisplayName(globalLabelsets?.image_issues, issue, i18n.language) ||
                          issue.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
                        return (
                          <span key={index} className='bale-image-quality-issue'>
                            {issueDisplayName}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        {/* Labeling panel - on large devices show panel next to bale image */}
        {currentUser.info.user_flags &&
          ['labeler', 'admin'].some((flag) => currentUser.info.user_flags.includes(flag)) &&
          userSettings.show_labeling_panel &&
          !isMobileOnly && (
            <LabelingPanel
              labelItem={bales[activeBaleId]}
              docRef={activeBaleDocRef}
              getNext={getNextBale}
              currentUserUid={currentUser.uid}
              currentUserInfoFlags={currentUser.info.user_flags}
              currentUserClientId={currentUser.company.company}
              labelsets={defaultLabelsets}
              firestore={firestore}
              searchableDropdowns={false}
              style={{
                height: '30rem',
              }}
            />
          )}
      </div>

      <div className='bale--footer'>
        <button className='bale-swipe-button prev' onClick={getPreviousBale}>
          <img src={back_icon} />
        </button>
        <div className='bale--properties'>
          <PropertyField
            icon={notes_icon}
            title={t('property_notes')}
            value={bales[activeBaleId]?.notes}
            name='notes'
            type='textarea'
            handleChange={handleFieldChange}
            editable={true}
            grid_column_span={2}
            grid_row_span={2}
          />
        </div>
        <button className='bale-swipe-button next' onClick={getNextBale}>
          <img src={back_icon} />
        </button>
      </div>

      {/* Labeling panel - on mobile devices show panel in a modal */}
      <Modal
        show={userSettings.show_labeling_panel && isMobileOnly}
        onHide={toggleLabelingView}
        backdrop='static'
        className='modal'
      >
        <div className='modal-box labeling-panel-modal'>
          <div className='close-button' onClick={toggleLabelingView}>
            <img src={close_icon} />
          </div>
          <LabelingPanel
            labelItem={bales[activeBaleId]}
            docRef={activeBaleDocRef}
            getNext={getNextBale}
            currentUserUid={currentUser.uid}
            currentUserInfoFlags={currentUser.info.user_flags}
            currentUserClientId={currentUser.company.company}
            labelsets={defaultLabelsets}
            firestore={firestore}
            searchableDropdowns={false}
          />
        </div>
      </Modal>
    </div>
  );
}
